import React from 'react';
import { AiOutlinePlus } from 'react-icons/ai';
import { FaRegTrashAlt } from 'react-icons/fa';

export interface RepeatableFieldListProps<T> {
    id?: string;
    label: string;
    items: T[];
    renderItem: (item: T, index: number, update: (patch: Partial<T>) => void) => React.ReactNode;
    onAdd: () => void;
    onRemove: (index: number) => void;
    onUpdate: (index: number, patch: Partial<T>) => void;
    addLabel?: string;
    emptyLabel?: string;
    maxItems?: number;
    disabled?: boolean;
}

function RepeatableFieldList<T>({
    id,
    label,
    items,
    renderItem,
    onAdd,
    onRemove,
    onUpdate,
    addLabel = 'Adicionar',
    emptyLabel = 'Nenhum item adicionado',
    maxItems,
    disabled = false,
}: RepeatableFieldListProps<T>) {
    const canAdd = !disabled && (maxItems === undefined || items.length < maxItems);

    return (
        <div id={id} className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{label}</span>
                <button
                    type="button"
                    onClick={onAdd}
                    disabled={!canAdd}
                    aria-label={addLabel}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                    <AiOutlinePlus aria-hidden="true" />
                    {addLabel}
                </button>
            </div>

            {items.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500 italic">{emptyLabel}</p>
            ) : (
                <ul className="flex flex-col gap-3">
                    {items.map((item, index) => (
                        <li
                            key={index}
                            className="flex items-start gap-2 p-3 border border-gray-200 dark:border-slate-700 rounded-lg"
                        >
                            <div className="flex-1">
                                {renderItem(item, index, patch => onUpdate(index, patch))}
                            </div>
                            <button
                                type="button"
                                onClick={() => onRemove(index)}
                                disabled={disabled}
                                aria-label={`Remover item ${index + 1}`}
                                className="mt-1 p-2 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
                            >
                                <FaRegTrashAlt aria-hidden="true" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default RepeatableFieldList;
